// GPU ブラシエンジン。ストローク中はカバレッジ (R8、文書サイズ) にダブを蓄積し、
// 合成時に Compositor が PAINT パスでレイヤーに乗せる (ライブ表示)。
// 確定時は同じ PAINT パスの結果を readPixels してレイヤーへ書き戻す。

import type { Rect } from '@/types/geometry'
import { GLProgram } from '@/gl/program'
import { createFramebuffer, deleteFramebuffer, type Framebuffer, type Texture } from '@/gl/texture'
import { docToClipMatrix } from '@/render/shaders/common'
import { DAB_VERT, DAB_FRAG } from '@/render/shaders/brush'
import { DabGenerator, PullStringSmoother, dabsBounds, type Dab, type StrokeSample } from './dabs'
import { tipToSquare, type BrushTip } from './tips'
import { generateRoundTip } from './tip'
import { rectIntersect, rectUnion } from '@/types/geometry'

export interface StrokeParams {
  layerId: string
  /** 0..1 */
  color: [number, number, number]
  opacity: number
  flow: number
  size: number
  hardness: number
  spacing: number
  /** スムージング半径 (px、0 で無効) */
  smoothing: number
  mode: 'paint' | 'erase' | 'clone' | 'dodge' | 'burn' | 'sponge' | 'desponge' | 'blur' | 'sharpen'
  /** clone: 現在位置 − ソース位置 (文書 px) */
  cloneOffset?: { x: number; y: number }
  /** dodge/burn/sponge/blur/sharpen の強さ 0..1 */
  strength?: number
  pencil: boolean
  pressureSize: boolean
  pressureFlow: boolean
  lockTransparency: boolean
  /** 先端の回転 (度) */
  angle?: number
  /** 真円率 0.05..1 */
  roundness?: number
  scatter?: number
  sizeJitter?: number
  /** カスタム先端 (null なら丸) */
  tip?: BrushTip | null
}

const TIP_SIZE = 128

export class BrushEngine {
  private program: GLProgram
  private vao: WebGLVertexArrayObject
  private unitVbo: WebGLBuffer
  private instVbo: WebGLBuffer
  private tipTex: WebGLTexture | null = null
  private tipHardness = -1
  private tipKey = ''
  private coverage: Framebuffer | null = null
  private covSize = { w: 0, h: 0 }
  private gen: DabGenerator | null = null
  private smoother: PullStringSmoother | null = null
  private bounds: Rect | null = null
  private inst = new Float32Array(4 * 1024)
  params: StrokeParams | null = null

  constructor(private gl: WebGL2RenderingContext) {
    this.program = new GLProgram(gl, DAB_VERT, DAB_FRAG, 'dab')
    const vao = gl.createVertexArray()!
    const unitVbo = gl.createBuffer()!
    const instVbo = gl.createBuffer()!
    this.vao = vao
    this.unitVbo = unitVbo
    this.instVbo = instVbo
    gl.bindVertexArray(vao)
    gl.bindBuffer(gl.ARRAY_BUFFER, unitVbo)
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([0, 0, 1, 0, 0, 1, 1, 1]), gl.STATIC_DRAW)
    gl.enableVertexAttribArray(0)
    gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0)
    gl.bindBuffer(gl.ARRAY_BUFFER, instVbo)
    gl.bufferData(gl.ARRAY_BUFFER, this.inst.byteLength, gl.DYNAMIC_DRAW)
    // aCenter (2), aSize (1), aAlpha (1) = 16 bytes stride
    gl.enableVertexAttribArray(1)
    gl.vertexAttribPointer(1, 2, gl.FLOAT, false, 16, 0)
    gl.vertexAttribDivisor(1, 1)
    gl.enableVertexAttribArray(2)
    gl.vertexAttribPointer(2, 1, gl.FLOAT, false, 16, 8)
    gl.vertexAttribDivisor(2, 1)
    gl.enableVertexAttribArray(3)
    gl.vertexAttribPointer(3, 1, gl.FLOAT, false, 16, 12)
    gl.vertexAttribDivisor(3, 1)
    gl.bindVertexArray(null)
  }

  get active(): boolean {
    return this.params !== null
  }

  get coverageTexture(): Texture | null {
    return this.coverage?.texture ?? null
  }

  /** これまでのダブの境界 (文書座標)。まだ無ければ null */
  get strokeBounds(): Rect | null {
    return this.bounds
  }

  private ensureTip(hardness: number, tip: BrushTip | null | undefined) {
    const gl = this.gl
    const key = tip ? `tip:${tip.id}` : `round:${hardness}`
    if (this.tipTex && this.tipKey === key) return
    if (!this.tipTex) this.tipTex = gl.createTexture()
    let size = TIP_SIZE
    let data: Uint8Array
    if (tip) {
      const sq = tipToSquare(tip)
      size = sq.size
      data = sq.data
    } else data = generateRoundTip(TIP_SIZE, hardness)
    this.tipKey = key
    gl.bindTexture(gl.TEXTURE_2D, this.tipTex)
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, size, size, 0, gl.RED, gl.UNSIGNED_BYTE, data)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.bindTexture(gl.TEXTURE_2D, null)
    this.tipHardness = hardness
  }

  private ensureCoverage(w: number, h: number) {
    const gl = this.gl
    if (this.coverage && this.covSize.w === w && this.covSize.h === h) return
    deleteFramebuffer(gl, this.coverage)
    // R8 の FBO
    const tex = gl.createTexture()!
    gl.bindTexture(gl.TEXTURE_2D, tex)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.R8, w, h, 0, gl.RED, gl.UNSIGNED_BYTE, null)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    const fbo = gl.createFramebuffer()!
    gl.bindFramebuffer(gl.FRAMEBUFFER, fbo)
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, tex, 0)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    this.coverage = { fbo, texture: { tex, width: w, height: h, channels: 1 } }
    this.covSize = { w, h }
  }

  begin(params: StrokeParams, docW: number, docH: number): void {
    const gl = this.gl
    this.params = params
    this.ensureTip(params.hardness, params.tip)
    this.ensureCoverage(docW, docH)
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.coverage!.fbo)
    gl.clearColor(0, 0, 0, 0)
    gl.clear(gl.COLOR_BUFFER_BIT)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    this.gen = new DabGenerator({ size: params.size, spacing: params.spacing, flow: params.flow, pressureSize: params.pressureSize, pressureFlow: params.pressureFlow, minSize: 0.1, scatter: params.scatter, sizeJitter: params.sizeJitter })
    this.smoother = new PullStringSmoother(params.smoothing)
    this.bounds = null
  }

  /** サンプルを追加してダブを描く。描いたダブの境界を返す (無ければ null) */
  addSample(s: StrokeSample): Rect | null {
    if (!this.gen || !this.smoother) return null
    const p = this.smoother.feed({ x: s.x, y: s.y })
    if (!p) return null
    return this.drawDabs(this.gen.feed({ x: p.x, y: p.y, pressure: s.pressure }))
  }

  /** ペンアップ: スムージングの遅れを追いつかせる */
  finish(s: StrokeSample): Rect | null {
    if (!this.gen || !this.smoother) return null
    const p = this.smoother.finish({ x: s.x, y: s.y })
    if (!p) return null
    return this.drawDabs(this.gen.feed({ x: p.x, y: p.y, pressure: s.pressure }))
  }

  private drawDabs(dabs: Dab[]): Rect | null {
    if (!dabs.length || !this.coverage || !this.params) return null
    const gl = this.gl
    const { w, h } = this.covSize
    if (dabs.length * 4 > this.inst.length) {
      this.inst = new Float32Array(Math.max(dabs.length * 4, this.inst.length * 2))
      gl.bindBuffer(gl.ARRAY_BUFFER, this.instVbo)
      gl.bufferData(gl.ARRAY_BUFFER, this.inst.byteLength, gl.DYNAMIC_DRAW)
    }
    for (let i = 0; i < dabs.length; i++) {
      const d = dabs[i]!
      this.inst[i * 4] = d.x
      this.inst[i * 4 + 1] = d.y
      this.inst[i * 4 + 2] = d.size
      this.inst[i * 4 + 3] = d.alpha
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.coverage.fbo)
    gl.viewport(0, 0, w, h)
    gl.enable(gl.BLEND)
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA)
    this.program.use()
    gl.uniformMatrix3fv(this.program.loc('uToClip'), false, docToClipMatrix(w, h))
    gl.uniform1i(this.program.loc('uPencil'), this.params.pencil ? 1 : 0)
    gl.uniform1f(this.program.loc('uAngle'), ((this.params.angle ?? 0) * Math.PI) / 180)
    gl.uniform1f(this.program.loc('uRoundness'), Math.max(0.05, Math.min(1, this.params.roundness ?? 1)))
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, this.tipTex)
    gl.uniform1i(this.program.loc('uTip'), 0)
    gl.bindVertexArray(this.vao)
    gl.bindBuffer(gl.ARRAY_BUFFER, this.instVbo)
    gl.bufferSubData(gl.ARRAY_BUFFER, 0, this.inst, 0, dabs.length * 4)
    gl.drawArraysInstanced(gl.TRIANGLE_STRIP, 0, 4, dabs.length)
    gl.bindVertexArray(null)
    gl.disable(gl.BLEND)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    const b = dabsBounds(dabs)!
    const clipped = rectIntersect(b, { x: 0, y: 0, w, h })
    if (clipped) this.bounds = rectUnion(this.bounds, clipped)
    return clipped
  }

  /** カバレッジ (0..255) を矩形で読み出す */
  readCoverage(rect: Rect): Uint8ClampedArray {
    const gl = this.gl
    const out = new Uint8ClampedArray(rect.w * rect.h)
    if (!this.coverage) return out
    const rgba = new Uint8Array(rect.w * rect.h * 4)
    gl.bindFramebuffer(gl.FRAMEBUFFER, this.coverage.fbo)
    gl.pixelStorei(gl.PACK_ALIGNMENT, 1)
    gl.readPixels(rect.x, rect.y, rect.w, rect.h, gl.RGBA, gl.UNSIGNED_BYTE, rgba)
    gl.bindFramebuffer(gl.FRAMEBUFFER, null)
    for (let i = 0; i < out.length; i++) out[i] = rgba[i * 4]!
    return out
  }

  end(): void {
    this.params = null
    this.gen = null
    this.smoother = null
  }

  dispose(): void {
    const gl = this.gl
    this.program.dispose()
    gl.deleteVertexArray(this.vao)
    gl.deleteBuffer(this.unitVbo)
    gl.deleteBuffer(this.instVbo)
    if (this.tipTex) gl.deleteTexture(this.tipTex)
    deleteFramebuffer(gl, this.coverage)
    this.coverage = null
    this.params = null
  }
}
